import { Button, Form, FormGroup, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import BlindStructureLevelsTable from './BlindStructureLevelsTable';
import useBlindStructures from '@/hooks/useBlindStructures';
import useTournamentTemplates from '@/hooks/useTournamentTemplates';
import { createBlindStructure, updateBlindStructure } from '@/redux/slices/tournaments';
import * as Yup from 'yup';
import FormInput from '@/components/forms/FormInput';

import { yupResolver } from '@hookform/resolvers/yup';

const getValidationSchema = ({
  blindStructures,
  selectedBlindStructure,
}: {
  blindStructures: any[];
  selectedBlindStructure?: any;
}) => {
  return Yup.object().shape({
    name: Yup.string()
      .required('Name is required')
      .test('name-unique', 'Name must be unique', (value, context) => {
        if (context.parent.id === selectedBlindStructure?.id) {
          return true;
        }
        return blindStructures.every((blindStructure: any) => blindStructure.name !== value);
      }),
    levels: Yup.array().of(
      Yup.object().shape({
        levelNumber: Yup.number().required('Level number is required'),
        smallBlind: Yup.number()
          .min(0, 'Small blind must be greater than 0')
          .required('Small blind is required')
          .test('small-blind-valid', 'Small blind must be less than big blind field level', (value, context) => {
            if (value > context.parent.bigBlind) {
              return context.createError({
                message: `Level ${context.parent.levelNumber} - Small blind must be less than big blind field`,
                params: {
                  levelNumber: context.parent.levelNumber,
                  field: 'smallBlind',
                },
              });
            }
            return true;
          }),
        bigBlind: Yup.number()
          .min(0, 'Big blind must be greater than 0')
          .required('Big blind is required')
          .test('big-blind-valid', 'Small blind must be less than big blind field level', (value, context) => {
            if (value < context.parent.smallBlind) {
              return context.createError({
                message: `Level ${context.parent.levelNumber} - Big blind must be greater than small blind field`,
                params: {
                  levelNumber: context.parent.levelNumber,
                  field: 'bigBlind',
                },
              });
            }
            return true;
          }),
        ante: Yup.number().min(0, 'Ante must be greater than 0'),
        smallBet: Yup.number()
          .min(0, 'Small bet must be greater than 0')
          .test('small-bet-valid', 'Small bet must be less than big bet', (value, context) => {
            if (value && value > context.parent.bigBet) {
              return context.createError({
                message: `Level ${context.parent.levelNumber} - Small bet must be less than big bet`,
                params: {
                  levelNumber: context.parent.levelNumber,
                  field: 'smallBet',
                },
              });
            }
            return true;
          }),
        bigBet: Yup.number()
          .min(0, 'Big bet must be greater than 0')
          .test('big-bet-valid', 'Big bet must be greater than small bet', (value, context) => {
            if (value && value < context.parent.smallBet) {
              return context.createError({
                message: `Level ${context.parent.levelNumber} - Big bet must be greater than small bet`,
                params: {
                  levelNumber: context.parent.levelNumber,
                  field: 'bigBet',
                },
              });
            }
            return true;
          }),
        bringIn: Yup.number()
          .min(0, 'Bring in must be greater than 0')
          .test('bring-in-valid', 'Bring in must be less than small bet', (value, context) => {
            if (value && value > context.parent.smallBet) {
              return context.createError({
                message: `Level ${context.parent.levelNumber} - Bring in must be less than small bet`,
                params: {
                  levelNumber: context.parent.levelNumber,
                  field: 'bringIn',
                },
              });
            }
            return true;
          }),
      }),
    ),
  });
};

const BlindStructureModalForm = ({
  onClose,
  selectedBlindStructureId,
}: {
  onClose: () => void;
  selectedBlindStructureId: string | null;
}) => {
  const dispatch = useDispatch();
  const { blindStructures } = useBlindStructures();

  const { tournamentTemplates: mttTemplates } = useTournamentTemplates();
  const { tournamentTemplates: sngTemplates } = useTournamentTemplates({ sng: true });

  const templates = [...(mttTemplates ?? []), ...(sngTemplates ?? [])];

  const selectedBlindStructure: any = blindStructures?.find(
    (blindStructure) => blindStructure.id === selectedBlindStructureId,
  );

  const form = useForm({
    defaultValues: selectedBlindStructure ?? {
      levels: [
        {
          levelNumber: 1,
          bigBlind: 0,
          smallBlind: 0,
          ante: 0,
          smallBet: 0,
          bigBet: 0,
          bringIn: 0,
        },
      ],
      name: null,
    },
    resolver: yupResolver(
      getValidationSchema({
        blindStructures: blindStructures ?? [],
        selectedBlindStructure,
      }),
    ),
    mode: 'onChange',
  });

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = form;

  const onSubmit = async (data: any) => {
    if (selectedBlindStructure) {
      const affectedTemplates = templates.filter(
        (tournament) => tournament.blindStructureId === selectedBlindStructureId,
      );
      dispatch(
        updateBlindStructure({
          id: data.id,
          levels: data.levels,
          name: data.name,
          meta: {
            toast: {
              success: 'Blind structure updated successfully',
              error: 'Error updating blind structure',
            },
            ...(affectedTemplates.length > 0
              ? {
                  confirm: {
                    title: 'Update Blind Structure',
                    subtitle:
                      'Are you sure you want to update the blind structure? There may be tournaments that may be affected by this change.',
                    confirmButton: {
                      text: 'Yes, update blind structure',
                      variant: 'primary',
                    },
                    cancelButton: {
                      text: 'Cancel',
                      variant: 'secondary',
                    },
                  },
                }
              : {}),
          },
        }),
      );
    } else {
      const blindStructureLevels = Object.keys(data.levels);
      const formattedBlindStructureLevels = blindStructureLevels.reduce((acc: any, curr) => {
        acc.push({
          ...data.levels[curr],
          levelNumber: Number(curr) + 1,
        });
        return acc;
      }, []);

      dispatch(
        createBlindStructure({
          name: data.name,
          levels: formattedBlindStructureLevels,
          meta: {
            toast: {
              success: 'Blind structure created successfully',
              error: 'Error creating blind structure',
            },
          },
        }),
      );
    }
    onClose();
  };

  return (
    <FormProvider {...form}>
      <Modal isOpen toggle={onClose} id="create-blind-structure" className="modal-lg" centered>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>{selectedBlindStructure ? 'Edit Blind Structure' : 'Create New Blind Structure'}</ModalHeader>
          <ModalBody style={{ maxHeight: '75vh' }} className="overflow-auto">
            <div className="w-100 d-flex flex-column justify-content-start max-h-[75vh] gap-2 overflow-auto">
              <div className="w-100 d-flex justify-content-between flex-row gap-2">
                <FormGroup className="w-100 h-100">
                  <Label for="name">Name</Label>
                  <FormInput control={control} name="name" type="text" />
                </FormGroup>
              </div>
              <BlindStructureLevelsTable />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="grey" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSubmit(onSubmit)} disabled={!isValid} color="primary">
              {selectedBlindStructure ? 'Save' : 'Create New Blind Structure'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </FormProvider>
  );
};

export default BlindStructureModalForm;
